import React, { useState, useCallback } from 'react';
import { func, instanceOf, bool, string, number } from 'prop-types';

import { Icon, DatePicker } from '@atoms';
import { Flex, Button, Typography } from '@mixins';
import { theme } from '@styles';
import { dayjs } from '@utils';

import { ReactComponent as Close } from '@assets/svgs/Close.svg';

const DateSelectingModal = ({
  canBeClosed,
  onSelect,
  calendarDate,
  onModalClose,
  type,
  maxDate,
  topPosition,
  hasSelectingButton,
  minDate,
  tileDisabled,
  onClear,
  clearLabel,
}) => {
  const [selectedDate, setSelectedDate] = useState(calendarDate);

  const handleDateSelect = useCallback(newDate => {
    setSelectedDate(newDate);
  }, []);

  const handleModalClose = useCallback(() => {
    setSelectedDate(null);
    onModalClose();
  }, []);

  const handleClear = useCallback(() => {
    setSelectedDate(null);
    onClear();
  }, [onClear]);

  return (
    <Flex
      zIndex={3}
      backgroundColor={theme.color.white}
      boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
      flexDirection="column"
      alignItems="center"
      position="absolute"
      top={topPosition}
      left={0}
      p={type === 'month' ? 20 : 18}
      pt={canBeClosed || onClear ? 45 : 20}>
      {onClear && (
        <Typography
          pointer
          onClick={handleClear}
          position="absolute"
          top={17}
          right={17}
          variant="diatype-400"
          fontSize={14}
          color={theme.color.mintMinded[900]}>
          {clearLabel}
        </Typography>
      )}
      {canBeClosed && (
        <Icon
          pointer
          onClick={handleModalClose}
          position="absolute"
          SVG={Close}
          width={11}
          height={11}
          {...(type === 'month' && { right: 17 })}
          {...(type === 'year' && { left: 17 })}
          top={17}
        />
      )}
      <DatePicker
        maxDate={maxDate}
        minDate={minDate}
        type={type}
        tileDisabled={tileDisabled}
        selectedDate={selectedDate}
        onDateSelect={hasSelectingButton ? handleDateSelect : onSelect}
      />
      {hasSelectingButton && (
        <Button mt={20} onClick={onSelect(selectedDate)} py={12} px={106} variant="primary">
          {type === 'month' ? 'Apply' : 'Select'}
        </Button>
      )}
    </Flex>
  );
};

DateSelectingModal.defaultProps = {
  maxDate: null,
  minDate: dayjs().$d,
  calendarDate: dayjs().$d,
  onModalClose: () => {},
  canBeClosed: true,
  hasSelectingButton: true,
  topPosition: 24,
  tileDisabled: null,
  onClear: null,
  clearLabel: 'Clear',
};

DateSelectingModal.propTypes = {
  minDate: instanceOf(Date),
  maxDate: instanceOf(Date),
  onSelect: func.isRequired,
  calendarDate: instanceOf(Date),
  onModalClose: func,
  canBeClosed: bool,
  type: string.isRequired,
  hasSelectingButton: bool,
  topPosition: number,
  tileDisabled: func,
  onClear: func,
  clearLabel: string,
};

export default DateSelectingModal;
